/**
 * Chart / task-state helpers for the AskAsper message processor.
 * Split out of messageProcessor.ts; used while folding SSE events into the loading message.
 */

import type { AgenticChatMessage, AgenticTask, AgenticHtmlChartPayload } from '../types';

export function hasChartPayload(item: AgenticHtmlChartPayload | null | undefined): boolean {
    if (!item) return false;
    if (item.viz_data) return true;
    return typeof item.html === 'string' && item.html.trim().length > 0;
}

export function normalizeChartPayloadItems(chartData: unknown): AgenticHtmlChartPayload[] {
    if (Array.isArray(chartData)) {
        return chartData.filter((item): item is AgenticHtmlChartPayload => hasChartPayload(item));
    }
    if (chartData && typeof chartData === 'object') {
        const item = chartData as AgenticHtmlChartPayload;
        if (hasChartPayload(item)) return [item];
    }
    return [];
}

/** Payload with task_id, task_name, run_id (shared by task_narrative, table, viz events) */
export type TaskEventPayload = { task_id: string; task_name: string; run_id?: string };

export function updateTaskLoadingState(
    loadingMessage: AgenticChatMessage,
    json: TaskEventPayload,
    key: 'narrative_loading' | 'table_loading' | 'viz_loading',
    value: boolean,
): AgenticChatMessage {
    const tasks = loadingMessage.additional_kwargs?.tasks ?? [];
    const existingIdx = tasks.findIndex((t) => t.task_id === json.task_id);
    const baseTask: AgenticTask =
        existingIdx >= 0
            ? (tasks[existingIdx] as AgenticTask)
            : {
                  task_id: json.task_id,
                  task_name: json.task_name,
                  status: 'executing',
              };
    const updatedTask = { ...baseTask, [key]: value };
    const updatedTasks =
        existingIdx >= 0
            ? tasks.map((t, i) => (i === existingIdx ? updatedTask : t))
            : [...tasks, updatedTask];
    return {
        ...loadingMessage,
        run_id: json.run_id ?? loadingMessage.run_id,
        additional_kwargs: {
            ...loadingMessage.additional_kwargs,
            tasks: updatedTasks,
        },
    };
}

/** Order charts first (BarChart, etc.), then tables. Applied after any chart update. */
export function chartsOrderedChartsFirstThenTables(charts: AgenticHtmlChartPayload[]): AgenticHtmlChartPayload[] {
    return [...charts].sort((a, b) => {
        const aIsTable = String(a.viz_data?.chart_type ?? '').toLowerCase() === 'table';
        const bIsTable = String(b.viz_data?.chart_type ?? '').toLowerCase() === 'table';
        if (aIsTable === bIsTable) return 0;
        return aIsTable ? 1 : -1; // non-table first
    });
}

export function replaceLoadingPlaceholder(
    charts: AgenticHtmlChartPayload[],
    taskId: string,
    chartType: string,
    vizData: any,
    taskName?: string,
): AgenticHtmlChartPayload[] {
    const typeStr = String(chartType ?? '').toLowerCase();
    let replaced = false;
    const result = charts.map((c) => {
        if (replaced) return c;
        const placeholderType = String(c.viz_data?.chart_type ?? '').toLowerCase();
        const isMatch =
            c.task_id === taskId &&
            c.viz_data?._loading === true &&
            (typeStr === 'table' ? placeholderType === 'table' : placeholderType !== 'table');
        if (isMatch) {
            replaced = true;
            return { ...c, viz_data: vizData, task_id: taskId, task_name: taskName ?? c.task_name };
        }
        return c;
    });
    if (!replaced) {
        result.push({ viz_data: vizData, task_id: taskId, task_name: taskName } as AgenticHtmlChartPayload);
    }
    return result;
}

/** Strip a `_viz_N` suffix (added below when a single event carries multiple charts) so a
 * repeated event for the same task_id is recognized as an update, not a new chart. */
function stripVizSuffix(taskId: string): string {
    return taskId.replace(/_viz_\d+$/, '');
}

function hasScenarioLinkData(item: AgenticHtmlChartPayload): boolean {
    return (
        typeof item.scenario_link === 'string' ||
        typeof item.scenarioLink === 'string' ||
        (Array.isArray(item.scenarioLinks) && item.scenarioLinks.length > 0)
    );
}

function isLoadingPlaceholderFor(
    chart: AgenticHtmlChartPayload,
    taskId: string,
    chartTypeStr: string,
): boolean {
    if (chart.task_id !== taskId || chart.viz_data?._loading !== true) return false;
    const placeholderType = String(chart.viz_data?.chart_type ?? '').toLowerCase();
    return chartTypeStr === 'table' ? placeholderType === 'table' : placeholderType !== 'table';
}

/** If `oldChart` is an earlier delivery of one of the `newCharts` items (same base task_id)
 * and carries scenario_link(s) the replacement is missing, copy those fields onto the
 * replacement so a link-less repeat delivery can't erase an earlier link. */
function carryForwardScenarioLinks(oldChart: AgenticHtmlChartPayload, newCharts: AgenticHtmlChartPayload[]): void {
    const replacement = newCharts.find((n) => n.task_id === oldChart.task_id);
    if (!replacement || hasScenarioLinkData(replacement) || !hasScenarioLinkData(oldChart)) return;
    replacement.scenario_link = oldChart.scenario_link;
    replacement.scenarioLink = oldChart.scenarioLink;
    replacement.scenarioLinks = oldChart.scenarioLinks;
}

function isTableChart(chart: AgenticHtmlChartPayload): boolean {
    return String(chart.viz_data?.chart_type ?? '').toLowerCase() === 'table';
}

/** True if `chart` is an earlier delivery of a table/chart being replaced by `newCharts`
 * for this task (matched by base task_id, ignoring any `_viz_N` suffix, AND by chart kind -
 * a task's table and its chart share one task_id and must never supersede each other). */
function isSupersededByNewCharts(
    chart: AgenticHtmlChartPayload,
    taskId: string,
    isNewBatchTable: boolean,
    newCharts: AgenticHtmlChartPayload[],
    newTaskIds: Set<AgenticHtmlChartPayload['task_id']>,
): boolean {
    if (chart.task_id == null || !newTaskIds.has(chart.task_id)) return false;
    if (isTableChart(chart) !== isNewBatchTable) return false;
    const existingTaskId = typeof chart.task_id === 'string' ? stripVizSuffix(chart.task_id) : chart.task_id;
    if (existingTaskId !== taskId) return false;
    carryForwardScenarioLinks(chart, newCharts);
    return true;
}

/** Replace placeholder with multiple charts (e.g. table_data/chart_data as array).
 * If this task_id already has real (non-loading) chart entries - e.g. a duplicate
 * table_generation_end for the same task - they are merged by exact task_id (including
 * the per-table `_viz_N` suffix) instead of duplicated, keeping whichever delivery of a
 * given table actually carries scenario_link(s) so a link-less repeat can't erase it.
 */
export function replaceLoadingPlaceholderWithCharts(
    charts: AgenticHtmlChartPayload[],
    taskId: string,
    chartType: string,
    chartItems: AgenticHtmlChartPayload[],
    taskName?: string,
): AgenticHtmlChartPayload[] {
    const typeStr = String(chartType ?? '').toLowerCase();
    const filtered = chartItems.filter((item) => item?.viz_data);
    if (filtered.length === 0) return charts;

    const newCharts = filtered.map((v, vizIdx) => ({
        ...v,
        viz_data: v.viz_data,
        task_id: v.task_id ?? (filtered.length > 1 ? `${taskId}_viz_${vizIdx}` : taskId),
        task_name: v.task_name ?? taskName,
        scenario_link: typeof v.scenario_link === 'string' ? v.scenario_link : undefined,
        scenarioLink: typeof v.scenarioLink === 'string' ? v.scenarioLink : undefined,
        scenarioLinks: Array.isArray(v.scenarioLinks) ? v.scenarioLinks : undefined,
    }));
    const newTaskIds = new Set(newCharts.map((c) => c.task_id));
    const isNewBatchTable = typeStr === 'table';

    let placeholderRemoved = false;
    const survivors = charts.filter((c) => {
        if (!placeholderRemoved && isLoadingPlaceholderFor(c, taskId, typeStr)) {
            placeholderRemoved = true;
            return false;
        }
        return !isSupersededByNewCharts(c, taskId, isNewBatchTable, newCharts, newTaskIds);
    });
    return [...survivors, ...newCharts];
}
